import { useEffect, useMemo, useState } from "react"
import Grid from "@mui/material/Grid"
import Icon from "@mui/material/Icon"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDNumberInput from "./components/MDNumberInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import SectionCard from "./components/SectionCard"
import StatusBadge from "./components/StatusBadge"
import CollapsibleCard from "./components/CollapsibleCard"
import RuleEditor from "./components/approvalRules/RuleEditor"
import RuleEditorStepper, {
  EDITOR_STEPS,
} from "./components/approvalRules/RuleEditorStepper"
import RuleEditorHeader from "./components/approvalRules/RuleEditorHeader"
import RuleListFilters from "./components/approvalRules/RuleListFilters"
import RuleListCard from "./components/approvalRules/RuleListCard"
import ResolvedChainPreview from "./components/approvalRules/ResolvedChainPreview"
import ConditionCheckPreview from "./components/approvalRules/ConditionCheckPreview"
import StickyActionFooter from "./components/approvalRules/StickyActionFooter"
import {
  RULE_ENTITY_OPTIONS,
  REGION_OPTIONS,
  findApproverOption,
  formatVND,
} from "./constants"
import { makeEmptyRule } from "./services/approvalRules"
import {
  useGetApprovalRulesQuery,
  useGetApprovalRuleQuery,
  useSaveApprovalRuleMutation,
  usePublishApprovalRuleMutation,
  useToggleApprovalRuleActiveMutation,
  usePreviewApprovalChainQuery,
  useGetActiveBudgetCategoriesQuery,
} from "shared/redux/endpoints/procurementV2"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { approvalRuleIssue } from "./fieldRules"

const ENTITY_LABEL = RULE_ENTITY_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label
  return acc
}, {})

const NOTIFIER_NOT_FOUND_RE = /Notifier email not found:\s*([^\s,]+)/i

const normalizeForCompare = (rule) => {
  if (!rule) return null
  const ruleData = rule.rule || rule
  const { id, ruleGroupId, publishedAt, createdAt, updatedAt, notifiers, notifierEmails, ...ruleRest } =
    ruleData
  const { version, isDraft, ...rest } = rule
  // `notifierEmails` (editable string) and `notifiers` (raw API list) both
  // describe the same thing — collapse to one shape so a freshly loaded rule
  // (only has `notifiers`) compares equal to itself once normalized into the
  // editor (has both).
  const notifierEmailsNormalized =
    notifierEmails !== undefined ? notifierEmails : (notifiers || []).map((n) => n.email).join(",")
  return JSON.stringify({ ...ruleRest, notifierEmails: notifierEmailsNormalized, ...rest })
}

// API responses only carry `notifiers` (full user objects); the editor edits
// a comma-separated `notifierEmails` string like it already does for
// approverRef. Derive it once here so both the initial load and the
// post-save refresh show the same value.
const normalizeLoadedRule = (apiRule) => {
  if (!apiRule) return apiRule
  const ruleData = apiRule.rule || apiRule
  return {
    ...apiRule,
    rule: {
      ...ruleData,
      notifierEmails: (ruleData.notifiers || []).map((n) => n.email).join(","),
    },
  }
}

const ApprovalRulesContainer = () => {
  const [listFilters, setListFilters] = useState({
    entity: "",
    category: "",
    region: "",
  })
  // Separate from listFilters (which maps 1:1 to getApprovalRules query
  // params) — status filters the already-fetched list client-side, since
  // the list endpoint doesn't take an isActive param. Defaults to "active"
  // per product ask; not counted in the Filter button's badge since it's on
  // by default, not something the admin explicitly turned on.
  const [statusFilter, setStatusFilter] = useState("active")
  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState(null)
  const [draft, setDraft] = useState(null)
  const [editorStep, setEditorStep] = useState("details")
  const [publishedNote, setPublishedNote] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(true)

  // Real budget categories (real numeric `id`) — used for both the rule-list
  // category filter and the Preview & Test panel below, both of which send
  // `category` straight to real backend endpoints (getApprovalRules /
  // previewApprovalChain) that expect the actual budget_category.id, not the
  // hand-picked slug list used for report/list filters elsewhere.
  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )

  const { data: rules, isFetching } = useGetApprovalRulesQuery(listFilters)
  const { data: selectedRule } = useGetApprovalRuleQuery(selectedId, {
    skip: !selectedId,
  })
  const [saveRule, { isLoading: saving }] = useSaveApprovalRuleMutation()
  const [badNotifierEmail, setBadNotifierEmail] = useState(null)
  const [publish, { isLoading: publishing }] = usePublishApprovalRuleMutation()
  const [toggleActive, { isLoading: togglingActive }] = useToggleApprovalRuleActiveMutation()

  const handleToggleActive = async (rule, nextIsActive) => {
    try {
      await toggleActive({ id: rule.id, isActive: nextIsActive }).unwrap()
      showToastSuccess(nextIsActive ? "Rule activated." : "Rule deactivated.")
    } catch (e) {
      const msg = e?.data?.data?.message || e?.data?.message || e?.message || "Failed to update rule status."
      showToastError(msg)
    }
  }

  // Every Save Draft / Publish invalidates the rule tag, so selectedRule
  // refetches while the admin keeps editing later steps. Only replace the
  // draft when switching rules or when it has no unsaved edits — otherwise
  // a late refetch wipes what was entered in the previous steps.
  useEffect(() => {
    if (!selectedRule) return
    setDraft((d) => {
      const draftId = (d?.rule || d)?.id
      const loadedId = (selectedRule.rule || selectedRule).id
      if (
        draftId === loadedId &&
        normalizeForCompare(d) !== normalizeForCompare(selectedRule)
      ) {
        return d
      }
      return normalizeLoadedRule(selectedRule)
    })
  }, [selectedRule])

  const [test, setTest] = useState({
    category: "",
    amount: "",
    region: "",
    center: "",
  })
  const { data: preview } = usePreviewApprovalChainQuery(
    {
      entity: draft?.rule?.entityType || "order",
      category: test.category,
      region: test.region,
      center: test.center,
      amount: Number(test.amount) || 0,
    },
    { skip: !draft }
  )

  const isDirty = useMemo(() => {
    if (!draft) return false
    if (!selectedId) {
      return normalizeForCompare(draft) !== normalizeForCompare(makeEmptyRule())
    }
    if (!selectedRule) return true
    return normalizeForCompare(draft) !== normalizeForCompare(selectedRule)
  }, [draft, selectedId, selectedRule])

  const activeFilterCount = useMemo(
    () => Object.values(listFilters).filter(Boolean).length,
    [listFilters]
  )

  const stepIndex = Math.max(
    0,
    EDITOR_STEPS.findIndex((s) => s.key === editorStep)
  )
  const isFirstStep = stepIndex <= 0
  const isLastStep = stepIndex >= EDITOR_STEPS.length - 1
  const nextStep = EDITOR_STEPS[stepIndex + 1]
  const nextLabel = nextStep ? `Next: ${nextStep.label}` : "Next"

  const handleNewRule = () => {
    setSelectedId(null)
    setDraft(makeEmptyRule())
    setEditorStep("details")
    setPublishedNote(null)
  }

  const handleSelectRule = (id) => {
    setSelectedId(id)
    setEditorStep("details")
    setPublishedNote(null)
  }

  const handleRename = (name) =>
    setDraft((d) => (d ? { ...d, rule: { ...(d.rule || d), name } } : d))

  const handleNextStep = () => {
    if (nextStep) setEditorStep(nextStep.key)
  }

  const handleBackStep = () => {
    const prev = EDITOR_STEPS[stepIndex - 1]
    if (prev) setEditorStep(prev.key)
  }

  const handleCancel = () => {
    setDraft(null)
    setSelectedId(null)
    setEditorStep("details")
    setPublishedNote(null)
  }

  // Create only accepts { name, entityType } (see saveApprovalRule) — it can't
  // carry the conditions/steps already built in the editor. For a brand-new
  // rule (no id yet) this creates the shell first, patches the returned id
  // into the CURRENT local draft (never the create response itself, so
  // in-progress edits survive untouched), then immediately PUTs the real
  // content. Returns the final saved rule (always has conditions/steps).
  const persistDraft = async (currentDraft) => {
    let workingDraft = currentDraft
    const existingId = (currentDraft?.rule || currentDraft)?.id

    if (!existingId) {
      const created = await saveRule(currentDraft).unwrap()
      const newId = created?.rule?.id || created?.id
      if (!newId) throw new Error("Rule ID not returned.")
      workingDraft = {
        ...currentDraft,
        rule: { ...(currentDraft.rule || currentDraft), id: newId },
      }
      setSelectedId(newId)
    }

    return saveRule(workingDraft).unwrap()
  }

  // The backend rejects the whole save when a notifier email doesn't match a
  // real user ("Notifier email not found: xxx@...") — keep the draft intact
  // and just remember which address so its chip can be outlined in red.
  const flagBadNotifierEmail = (msg) =>
    setBadNotifierEmail(msg?.match(NOTIFIER_NOT_FOUND_RE)?.[1] || null)

  const handleSaveDraft = async () => {
    setBadNotifierEmail(null)
    const issue = approvalRuleIssue(draft)
    if (issue) {
      showToastError(issue)
      return
    }
    try {
      const res = await persistDraft(draft)
      const id = res?.rule?.id
      if (id) {
        setSelectedId(id)
        setDraft(normalizeLoadedRule(res))
        showToastSuccess("Draft saved successfully.")
      }
    } catch (e) {
      const msg = e?.data?.data?.message || e?.data?.message || e?.message || "Failed to save draft."
      showToastError(msg)
      flagBadNotifierEmail(msg)
    }
  }

  const handlePublish = async () => {
    setBadNotifierEmail(null)
    const issue = approvalRuleIssue(draft)
    if (issue) {
      showToastError(issue)
      return
    }
    try {
      const saved = await persistDraft(draft)
      const target = saved?.rule?.id || selectedId
      if (!target) {
        showToastError("Cannot publish: rule ID not found.")
        return
      }
      setSelectedId(target)
      const res = await publish(target).unwrap()
      if (res) {
        setPublishedNote(
          `v${res.version} published — applies to new requests only`
        )
        showToastSuccess(`v${res.version} published successfully.`)
      }
    } catch (e) {
      const msg = e?.data?.data?.message || e?.data?.message || e?.message || "Failed to publish rule."
      showToastError(msg)
      flagBadNotifierEmail(msg)
    }
  }

  const ruleList = useMemo(() => {
    let list = rules || []
    if (statusFilter === "active") list = list.filter((r) => r.isActive)
    else if (statusFilter === "inactive") list = list.filter((r) => !r.isActive)
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((r) => (r.name || "").toLowerCase().includes(q))
  }, [rules, search, statusFilter])

  const statusIsActive = Boolean((draft?.rule || draft)?.isActive) && !draft?.isDraft
  const statusLabel = statusIsActive ? "Active" : "Draft"

  const configuredStepsPreview = useMemo(
    () =>
      (draft?.steps || []).map((s) => ({
        stepOrder: s.stepOrder,
        // Emails steps aren't in the lookup, so this falls through to the raw
        // comma-separated address string the admin entered — ResolvedChainPreview
        // renders that as chips using approverType, not this label.
        approverLabel:
          s.approverType === "emails" ? s.approverRef : findApproverOption(s)?.label || s.approverRef,
        approverType: s.approverType,
        approverRef: s.approverRef,
        mode: s.mode,
        quorum: s.quorum,
        slaHours: s.slaHours,
        escalateTo: s.escalateTo,
        maxAmount: s.maxAmount,
      })),
    [draft?.steps]
  )

  const isNew = !selectedId
  const lastCrumb = draft
    ? isNew
      ? "Create new rule"
      : (draft.rule || draft).name?.trim() || "Edit rule"
    : "Rules"

  return (
    <DashboardLayout>
      <MDBox px={{ xs: 2, md: 3 }} pt={{ xs: 2, md: 3 }} pb={{ xs: 2, md: 3 }}>
        {/* Breadcrumb */}
        <MDBox
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={0.75}
          mb={{ xs: 2, md: 3 }}
        >
          <MDTypography variant="button" color="text" fontWeight="regular">
            Procurement Process
          </MDTypography>
          <Icon fontSize="small" sx={{ color: "text.secondary" }}>
            chevron_right
          </Icon>
          <MDTypography variant="button" color="text" fontWeight="regular">
            Rules
          </MDTypography>
          {draft && (
            <>
              <Icon fontSize="small" sx={{ color: "text.secondary" }}>
                chevron_right
              </Icon>
              <MDTypography
                variant="button"
                color="dark"
                fontWeight="bold"
                sx={{ wordBreak: "break-word" }}
              >
                {lastCrumb}
              </MDTypography>
            </>
          )}
        </MDBox>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={4} xl={3}>
            <MDBox
              bgColor="white"
              borderRadius="lg"
              shadow="sm"
              p={{ xs: 2, md: 2.5 }}
              display="flex"
              flexDirection="column"
              sx={{
                position: { lg: "sticky" },
                top: { lg: 24 },
                maxHeight: { lg: "calc(100vh - 48px)" },
              }}
            >
              <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                <MDBox
                  width="2.5rem"
                  height="2.5rem"
                  minWidth="2.5rem"
                  borderRadius="50%"
                  bgColor="info"
                  color="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  shadow="md"
                >
                  <Icon fontSize="small">list_alt</Icon>
                </MDBox>
                <MDTypography variant="h6" fontWeight="bold">
                  Rules
                </MDTypography>
                <MDTypography variant="caption" color="text" sx={{ ml: "auto" }}>
                  {ruleList.length}
                </MDTypography>
              </MDBox>

              <RuleListFilters
                search={search}
                onSearchChange={setSearch}
                filters={listFilters}
                onChange={setListFilters}
                entityOptions={RULE_ENTITY_OPTIONS}
                categoryOptions={categoryOptions}
                regionOptions={REGION_OPTIONS}
                activeCount={activeFilterCount}
                onCreate={handleNewRule}
                status={statusFilter}
                onStatusChange={setStatusFilter}
              />

              {/* Only the list scrolls — search, filters and the header stay put */}
              <MDBox
                mt={1}
                pr={0.5}
                sx={{
                  overflowY: "auto",
                  maxHeight: { xs: 360, sm: 420, lg: "none" },
                  minHeight: 0,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    borderRadius: 3,
                    backgroundColor: "rgba(0,0,0,0.16)",
                  },
                }}
              >
                {isFetching ? (
                  <MDTypography variant="body2" color="text">
                    Loading…
                  </MDTypography>
                ) : ruleList.length === 0 ? (
                  <MDBox textAlign="center" py={3}>
                    <MDTypography variant="body2" color="text">
                      No rules match your search.
                    </MDTypography>
                  </MDBox>
                ) : (
                  ruleList.map((r) => (
                    <RuleListCard
                      key={r.id}
                      rule={r}
                      selected={selectedId === r.id}
                      onSelect={() => handleSelectRule(r.id)}
                      entityLabelMap={ENTITY_LABEL}
                      onToggleActive={handleToggleActive}
                      togglingActive={togglingActive}
                    />
                  ))
                )}
              </MDBox>
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={8} xl={9}>
            {!draft ? (
              <SectionCard p={{ xs: 3, md: 5 }} mb={0}>
                <MDBox textAlign="center">
                  <MDBox
                    width="3.5rem"
                    height="3.5rem"
                    borderRadius="50%"
                    bgColor="grey-100"
                    color="info"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                  >
                    <Icon>rule</Icon>
                  </MDBox>
                  <MDTypography variant="h6" fontWeight="medium" mb={1}>
                    Select or create a rule
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    Choose a rule from the list to edit, or start a new approval
                    workflow.
                  </MDTypography>
                  <MDButton variant="gradient" color="info" onClick={handleNewRule}>
                    + New Rule
                  </MDButton>
                </MDBox>
              </SectionCard>
            ) : (
              <MDBox>
                {/* Rule identity + workflow stepper */}
                <MDBox
                  bgColor="white"
                  borderRadius="lg"
                  shadow="sm"
                  p={{ xs: 2, sm: 2.5, md: 3 }}
                  mb={3}
                >
                  <RuleEditorHeader
                    rule={draft}
                    isNew={isNew}
                    onRename={handleRename}
                    status={
                      <StatusBadge
                        status={statusIsActive ? "approved" : "new"}
                        label={statusLabel}
                        color={statusIsActive ? "success" : "warning"}
                      />
                    }
                  />
                  <MDBox
                    mt={2.5}
                    pt={2.5}
                    sx={{ borderTop: "1px solid", borderColor: "grey.300" }}
                  >
                    <RuleEditorStepper
                      activeStep={editorStep}
                      onStepChange={setEditorStep}
                      rule={draft}
                    />
                  </MDBox>
                </MDBox>

                {editorStep !== "preview" && (
                  <RuleEditor
                    rule={draft}
                    onChange={setDraft}
                    step={editorStep}
                    invalidNotifierEmail={badNotifierEmail}
                  />
                )}

                {editorStep === "preview" && (
                  <MDBox>
                    <SectionCard
                      eyebrow={`Step ${EDITOR_STEPS.length} of ${EDITOR_STEPS.length}`}
                      title="Your approval steps"
                      subtitle="The steps you set up — check they match the preview below."
                      icon="account_tree"
                      mb={3}
                    >
                      {configuredStepsPreview.length > 0 ? (
                        <ResolvedChainPreview steps={configuredStepsPreview} />
                      ) : (
                        <MDTypography variant="body2" color="text">
                          Add approval steps before publishing.
                        </MDTypography>
                      )}
                    </SectionCard>

                    <CollapsibleCard
                      title="Preview & Test"
                      subtitle="Try a sample request to see who would approve it"
                      icon="science"
                      open={previewOpen}
                      onToggle={() => setPreviewOpen((v) => !v)}
                    >
                      <MDTypography variant="caption" color="text" display="block" mb={2}>
                        Sample values only — they do not change the rule or the list filters.
                      </MDTypography>
                      <Grid container spacing={2} mb={3} alignItems="flex-start">
                        <Grid item xs={12} sm={6} lg={3}>
                          <MDSelect
                            isClearable
                            labelType="non-sticky"
                            labelText="Category"
                            placeholder="Any"
                            options={categoryOptions}
                            value={
                              categoryOptions.find(
                                (o) => o.value === test.category
                              ) || null
                            }
                            onChange={(o) =>
                              setTest((p) => ({
                                ...p,
                                category: o?.value || "",
                              }))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                          <MDNumberInput
                            fullWidth
                            labelType="non-sticky"
                            labelText="Amount"
                            placeholder="e.g. 25,000,000"
                            value={test.amount}
                            onChange={(v) => setTest((p) => ({ ...p, amount: v }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                          <MDSelect
                            isClearable
                            labelType="non-sticky"
                            labelText="Region"
                            placeholder="Any"
                            options={REGION_OPTIONS}
                            value={
                              REGION_OPTIONS.find(
                                (o) => o.value === test.region
                              ) || null
                            }
                            onChange={(o) =>
                              setTest((p) => ({
                                ...p,
                                region: o?.value || "",
                              }))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                          <MDInput
                            fullWidth
                            labelType="non-sticky"
                            labelText="Center"
                            placeholder="Any"
                            value={test.center}
                            onChange={(e) =>
                              setTest((p) => ({ ...p, center: e.target.value }))
                            }
                          />
                        </Grid>
                      </Grid>

                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="medium"
                        mb={1.5}
                        display="block"
                      >
                        Who will approve ({test.amount ? formatVND(test.amount) : "—"})
                        {preview?.matchedRuleId
                          ? ` — Rule #${preview.matchedRuleId} v${preview.matchedRuleVersion}`
                          : ""}
                      </MDTypography>
                      <ResolvedChainPreview steps={preview?.steps || []} />
                    </CollapsibleCard>
                  </MDBox>
                )}

                <ConditionCheckPreview rule={draft} savedRule={selectedRule} />

                <StickyActionFooter
                  isDirty={isDirty}
                  saving={saving}
                  publishing={publishing}
                  publishedNote={publishedNote}
                  isFirstStep={isFirstStep}
                  isLastStep={isLastStep}
                  nextLabel={nextLabel}
                  onBack={handleBackStep}
                  onNext={handleNextStep}
                  onCancel={handleCancel}
                  onSaveDraft={handleSaveDraft}
                  onPublish={handlePublish}
                />
              </MDBox>
            )}
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  )
}

export default ApprovalRulesContainer
